/**
 * Find-and-fix remediation library.
 *
 * The "find" half (the scan) is solid. The "fix" half is the differentiator —
 * for every issue surfaced by an evaluator, we provide a concrete next action:
 * the DNS record to add, the ESP doc to read, the delisting URL to file, etc.
 *
 * Issue codes come from the scan evaluators in api/src/lib/scan/. When a
 * scanner flags `code: 'spf_too_many_lookups'`, lookup() returns the matching
 * Fix. Codes without an entry fall through to `genericFix` so we never show a
 * raw issue with no guidance — that would break the tagline.
 *
 * Each Fix has:
 *   - title: short imperative ("Reduce SPF DNS lookups")
 *   - summary: 1-2 sentences explaining what's broken in plain English
 *   - steps: ordered remediation steps (UI renders as numbered list)
 *   - record?: a copy-pasteable DNS record snippet, when applicable
 *   - links?: external references (ESP docs, delisting URLs, RFC pointers)
 */

export interface FixLink {
  label: string;
  href: string;
}

export interface FixRecord {
  /** DNS host portion (e.g. "_dmarc.example.com") */
  host: string;
  /** DNS record type (TXT, CNAME, MX, …) */
  type: string;
  /** Record value the user pastes into their DNS provider. */
  value: string;
  /** Optional caveat ("replace example.com with your domain") */
  note?: string;
}

export interface Fix {
  code: string;
  category: 'spf' | 'dmarc' | 'dkim' | 'ptr' | 'mta-sts' | 'tls-rpt' | 'blocklist' | 'general';
  title: string;
  /** 1-2 sentences in plain English. */
  summary: string;
  /** Numbered remediation steps. */
  steps: string[];
  record?: FixRecord;
  links?: FixLink[];
  /** Estimated time to fix once you have DNS access. */
  effort?: '5 min' | '15 min' | '30 min' | '1 hour';
}

const FIXES: Record<string, Fix> = {
  // ---------- SPF ----------
  spf_missing: {
    code: 'spf_missing',
    category: 'spf',
    title: 'Publish an SPF record',
    summary:
      'No SPF record found. Without one, receivers have no list of IPs authorized to send for your domain — most will treat your mail as suspicious.',
    steps: [
      'Identify every service that sends mail "from" your domain (Google Workspace, M365, your CRM, your billing tool, etc.).',
      'Compose a single SPF record at the apex (no second record — Postfix and others will refuse mail with two SPF TXTs).',
      'Use the snippet below as a starting point and replace it with your actual senders.',
      'Publish as a TXT record at the apex (host = "@" or your domain itself).',
    ],
    record: {
      host: '@',
      type: 'TXT',
      value: 'v=spf1 include:_spf.google.com ~all',
      note: 'Replace _spf.google.com with your actual senders. End with ~all (softfail) until you\'re confident, then move to -all.',
    },
    links: [
      { label: 'RFC 7208 (SPF spec)', href: 'https://datatracker.ietf.org/doc/html/rfc7208' },
    ],
    effort: '15 min',
  },
  spf_too_many_lookups: {
    code: 'spf_too_many_lookups',
    category: 'spf',
    title: 'Reduce SPF DNS lookups (10 max)',
    summary:
      'SPF resolution requires ≤ 10 DNS lookups. Yours exceeds that, which means receivers will return PermError and treat your mail as un-authenticated.',
    steps: [
      'Use the SPF report below to see which `include:` chains are inflating your lookup count.',
      'Remove SPF includes for services you no longer use.',
      'Consolidate via "SPF flattening" — replace nested `include:` chains with explicit ip4:/ip6: ranges. Refresh nightly.',
      'If you can\'t flatten, restructure to put your most important sender first; receivers stop counting at 10.',
    ],
    links: [
      { label: 'Why SPF flattening', href: 'https://www.m3aawg.org/sites/default/files/m3aawg-spf-best-practices-2017-06.pdf' },
    ],
    effort: '30 min',
  },
  spf_softfail_or_neutral: {
    code: 'spf_softfail_or_neutral',
    category: 'spf',
    title: 'Tighten SPF policy (-all)',
    summary:
      'Your SPF ends in `~all` (softfail) or `?all` (neutral). Receivers won\'t reject unauthorized senders. Tighten to `-all` (hard fail) once you\'ve verified the include list is complete.',
    steps: [
      'Run a 14-day soak with `~all` and watch DMARC RUA reports for legitimate senders that fail SPF.',
      'Add any missed senders to your SPF record.',
      'Once RUA shows 100% pass, change `~all` to `-all`.',
    ],
    effort: '15 min',
  },
  spf_multiple_records: {
    code: 'spf_multiple_records',
    category: 'spf',
    title: 'Consolidate SPF into a single TXT record',
    summary:
      'You have two or more SPF TXT records at the apex. Receivers ignore both and you lose authentication entirely.',
    steps: [
      'Identify every TXT record starting with `v=spf1`.',
      'Merge their `include:`/`ip4:`/`ip6:` mechanisms into one record.',
      'Delete the duplicates. Verify with a fresh scan.',
    ],
    effort: '15 min',
  },

  // ---------- DMARC ----------
  dmarc_missing: {
    code: 'dmarc_missing',
    category: 'dmarc',
    title: 'Publish a DMARC record',
    summary:
      'No DMARC record at _dmarc.<domain>. Without one, you can\'t tell receivers what to do with un-authenticated mail and you get zero forensic visibility into spoofing.',
    steps: [
      'Start in monitor mode (p=none) so you don\'t accidentally drop legitimate mail before you\'ve verified your senders.',
      'Add the TXT record below at `_dmarc.<your-domain>`.',
      'Configure rua= to a mailbox you read (or InboxGuard\'s hosted RUA on Pro).',
      'After 14 days of clean reports, ratchet to p=quarantine, then p=reject.',
    ],
    record: {
      host: '_dmarc',
      type: 'TXT',
      value: 'v=DMARC1; p=none; rua=mailto:dmarc-reports@yourdomain.com; fo=1; aspf=r; adkim=r;',
    },
    links: [
      { label: 'DMARC.org getting started', href: 'https://dmarc.org/overview/' },
    ],
    effort: '15 min',
  },
  dmarc_p_none: {
    code: 'dmarc_p_none',
    category: 'dmarc',
    title: 'Move DMARC to enforcement (p=quarantine or p=reject)',
    summary:
      'Your DMARC policy is `p=none` — monitor only. Spoofers can still send mail "from" your domain and receivers won\'t reject it. This is the single biggest miss in deliverability hygiene.',
    steps: [
      'Confirm your RUA reports show ≥ 99% DMARC pass for the last 14 days.',
      'Move to p=quarantine (mail goes to spam) for 14 days. Watch RUA.',
      'Move to p=reject. Spoofed mail will be bounced.',
    ],
    effort: '15 min',
  },
  dmarc_pct_partial: {
    code: 'dmarc_pct_partial',
    category: 'dmarc',
    title: 'Remove the pct= rampup (or finish the rollout)',
    summary:
      'DMARC `pct=` partial enforcement is a roll-up tool, not a destination. Most receivers ignore it anyway — finish the rollout to get the full benefit.',
    steps: [
      'If you started at pct=10 and have stabilized, set pct=100 (or remove the tag — 100 is the default).',
    ],
    effort: '5 min',
  },

  // ---------- DKIM ----------
  dkim_no_selectors_found: {
    code: 'dkim_no_selectors_found',
    category: 'dkim',
    title: 'Set up DKIM signing',
    summary:
      'No DKIM selectors found at the common ESP locations. Without DKIM, you fail DMARC alignment when SPF breaks (e.g. on forwarded mail) and your mail loses cryptographic authentication entirely.',
    steps: [
      'Check your ESP\'s DKIM setup page — Google, M365, Mailgun, SendGrid, Postmark, ConvertKit, etc., all have one.',
      'Generate a 2048-bit key (1024 is deprecated and some receivers downgrade trust).',
      'Publish the TXT record they provide at `<selector>._domainkey.<your-domain>`.',
      'Wait for the ESP to confirm the key is verified, then enable signing on outbound mail.',
      'Re-scan to confirm DKIM is now visible.',
    ],
    links: [
      { label: 'Google DKIM setup', href: 'https://support.google.com/a/answer/180504' },
      { label: 'M365 DKIM setup', href: 'https://learn.microsoft.com/en-us/defender-office-365/email-authentication-dkim-configure' },
    ],
    effort: '30 min',
  },
  dkim_short_key: {
    code: 'dkim_short_key',
    category: 'dkim',
    title: 'Rotate to a 2048-bit DKIM key',
    summary:
      'Your DKIM key is < 2048 bits. Gmail and others actively downgrade trust on 1024-bit keys. Rotate to 2048 bits.',
    steps: [
      'Generate a new 2048-bit key in your ESP (most have a "rotate" button).',
      'Publish the new selector record.',
      'Once verified, mark the new selector active and remove the old TXT record.',
    ],
    effort: '15 min',
  },
  // score.ts v3 (docs/adr/0001-scoring-applicability.md): DKIM selectors
  // aren't discoverable via DNS at all — the selector only appears inside a
  // signed message's DKIM-Signature header. When SPF shows an ESP that mints
  // a random per-tenant selector (Amazon SES Easy DKIM, SparkPost), "no keys
  // found at the selectors we guessed" is NOT evidence DKIM is broken — it's
  // evidence our DNS-guessing heuristic structurally can't find it. This is a
  // VERIFY action, never a "go set this up" one, and never a critical/warn —
  // see IssueList.tsx's isUnverified handling.
  dkim_unverified: {
    code: 'dkim_unverified',
    category: 'dkim',
    title: 'Verify DKIM (selector not discoverable via DNS)',
    summary:
      "We couldn't verify DKIM from DNS. Your SPF pattern matches an ESP (e.g. Amazon SES Easy DKIM, SparkPost) that mints a random per-account DKIM selector only discoverable from a signed message — not by guessing. Excluded from your score rather than counted as a failure.",
    steps: [
      'Send yourself a test message from this domain and open "show original" / "view source" in your mail client.',
      'Copy the `DKIM-Signature: ... s=<selector>` value from the raw header — that\'s your selector.',
      'Using Amazon SES Easy DKIM? Open the SES console → Verified identities → this domain, and confirm all three CNAME records show Verified/green — that alone confirms DKIM is live even without a header sample.',
    ],
    links: [
      { label: 'Amazon SES Easy DKIM', href: 'https://docs.aws.amazon.com/ses/latest/dg/send-email-authentication-dkim-easy.html' },
    ],
    effort: '15 min',
  },

  // ---------- PTR / Reverse DNS ----------
  ptr_mismatch: {
    code: 'ptr_mismatch',
    category: 'ptr',
    title: 'Fix PTR (reverse DNS) mismatch',
    summary:
      'Your sending IP\'s PTR record doesn\'t match a forward record. Receivers (Gmail, Outlook) treat this as a strong "this is a botnet" signal.',
    steps: [
      'Get your sending IP from your ESP or smarthost (this is the IP that actually opens the SMTP connection to receivers).',
      'In your cloud provider or ISP\'s console, set the PTR record to a hostname you control (e.g. mail.yourdomain.com).',
      'Make sure that hostname has a forward A record back to the same IP.',
      'PTR propagation can take hours — re-scan tomorrow if it doesn\'t reflect immediately.',
    ],
    effort: '30 min',
  },

  // ---------- MTA-STS ----------
  mta_sts_missing: {
    code: 'mta_sts_missing',
    category: 'mta-sts',
    title: 'Publish an MTA-STS policy',
    summary:
      'MTA-STS forces senders to use TLS to your MX. Without it, your inbound mail can be downgraded to plaintext via a man-in-the-middle. Pro audit checklists flag this.',
    steps: [
      'Publish a `_mta-sts.<your-domain>` TXT record with a versioned ID.',
      'Host the policy file at `https://mta-sts.<your-domain>/.well-known/mta-sts.txt`.',
      'Start in mode=testing; once you confirm receivers can read it, ratchet to mode=enforce.',
    ],
    record: {
      host: '_mta-sts',
      type: 'TXT',
      value: 'v=STSv1; id=20260101000000Z',
      note: 'The id changes whenever the policy file changes — bump it on every edit.',
    },
    links: [
      { label: 'RFC 8461 (MTA-STS)', href: 'https://datatracker.ietf.org/doc/html/rfc8461' },
    ],
    effort: '1 hour',
  },

  // score.ts v3: the MX/DNS lookup itself failed this scan, so we don't know
  // whether MTA-STS applies — a "couldn't check, re-scan" item, not a "go
  // set this up" one.
  mta_sts_unverified: {
    code: 'mta_sts_unverified',
    category: 'mta-sts',
    title: 'Re-check MTA-STS (DNS lookup failed this scan)',
    summary:
      "We couldn't verify MTA-STS this scan — the DNS lookup failed (usually a transient resolver problem). Excluded from your score rather than counted as a failure.",
    steps: [
      'Re-scan in a few minutes — this is usually a transient resolver issue.',
      'If it persists, confirm _mta-sts.<your-domain> resolves via a public DNS tool (e.g. dig).',
    ],
    effort: '5 min',
  },

  // ---------- TLS-RPT ----------
  tls_rpt_missing: {
    code: 'tls_rpt_missing',
    category: 'tls-rpt',
    title: 'Publish a TLS-RPT record',
    summary:
      'TLS-RPT lets receivers tell you when TLS to your MX fails. Pairs with MTA-STS — without it you\'re flying blind on transport failures.',
    steps: [
      'Add the TXT record below.',
      'Point rua= to a mailbox or aggregator (Postmark, Valimail, or InboxGuard\'s hosted endpoint on Pro).',
    ],
    record: {
      host: '_smtp._tls',
      type: 'TXT',
      value: 'v=TLSRPTv1; rua=mailto:tls-reports@yourdomain.com',
    },
    effort: '5 min',
  },

  tls_rpt_unverified: {
    code: 'tls_rpt_unverified',
    category: 'tls-rpt',
    title: 'Re-check TLS-RPT (DNS lookup failed this scan)',
    summary:
      "We couldn't verify TLS-RPT this scan — the DNS lookup failed (usually a transient resolver problem). Excluded from your score rather than counted as a failure.",
    steps: [
      'Re-scan in a few minutes — this is usually a transient resolver issue.',
      'If it persists, confirm _smtp._tls.<your-domain> resolves via a public DNS tool (e.g. dig).',
    ],
    effort: '5 min',
  },

  // ---------- Blocklist ----------
  blocklist_listed: {
    code: 'blocklist_listed',
    category: 'blocklist',
    title: 'Request delisting from the blocklist',
    summary:
      'Your sending IP or domain is on a blocklist. Major receivers (especially smaller hosts using Spamhaus ZEN) will reject your mail outright until you\'re delisted.',
    steps: [
      'Visit the delisting URL for the specific blocklist below.',
      'Submit the delisting request — most lists require a cooldown of 24-48 hours of no spam complaints first.',
      'Investigate WHY you got listed: compromised credentials, a leaky form, a hijacked SaaS integration. Delisting without fixing the cause is temporary.',
      'Re-scan after the cooldown to confirm.',
    ],
    links: [
      { label: 'Spamhaus removal', href: 'https://www.spamhaus.org/lookup/' },
      { label: 'SpamCop removal', href: 'https://www.spamcop.net/bl.shtml' },
      { label: 'Barracuda removal', href: 'https://www.barracudacentral.org/rbl/removal-request' },
      { label: 'SORBS removal', href: 'https://www.sorbs.net/general/using.shtml' },
      { label: 'CBL removal', href: 'https://www.abuseat.org/lookup.cgi' },
    ],
    effort: '1 hour',
  },
};

const GENERIC_FIX: Fix = {
  code: 'generic',
  category: 'general',
  title: 'Issue detected',
  summary:
    'This issue doesn\'t have a canned remediation yet. The general approach is to verify the underlying DNS or transport configuration matches the receiver\'s expectations.',
  steps: [
    'Re-read the raw issue message above.',
    'Check your DNS provider for the relevant record.',
    'If you need a hand, email support — we\'ll add this code to the library.',
  ],
};

export function lookupFix(code: string | undefined | null): Fix {
  if (!code) return GENERIC_FIX;
  return FIXES[code] ?? GENERIC_FIX;
}

/** Heuristic: derive a fix code from an evaluator's issue message when the
 *  evaluator hasn't yet been updated to emit a structured `code`. Older
 *  evaluators only emit `severity` + `message`. */
export function inferFixCode(args: { category: Fix['category']; message: string }): string {
  const m = args.message.toLowerCase();
  switch (args.category) {
    case 'spf':
      if (m.includes('no spf') || m.includes('not found')) return 'spf_missing';
      if (m.includes('lookup')) return 'spf_too_many_lookups';
      if (m.includes('multiple')) return 'spf_multiple_records';
      if (m.includes('softfail') || m.includes('neutral')) return 'spf_softfail_or_neutral';
      return 'spf_missing';
    case 'dmarc':
      if (m.includes('no dmarc') || m.includes('not found')) return 'dmarc_missing';
      if (m.includes('p=none') || m.includes('monitor')) return 'dmarc_p_none';
      if (m.includes('pct=')) return 'dmarc_pct_partial';
      return 'dmarc_missing';
    case 'dkim':
      if (m.includes('1024')) return 'dkim_short_key';
      return 'dkim_no_selectors_found';
    case 'ptr':
      return 'ptr_mismatch';
    case 'mta-sts':
      return 'mta_sts_missing';
    case 'tls-rpt':
      return 'tls_rpt_missing';
    case 'blocklist':
      return 'blocklist_listed';
    default:
      return 'generic';
  }
}
